import { useEffect, useState } from 'react'
import type { AdminAppState } from '../hooks/useAdminApp'
import { fetchAdminReleaseNotes } from '../lib/api/backoffice/release'
import {
  isDashboardModuleUnavailable,
  isModuleUnavailableError,
  markDashboardModuleAvailable,
  markDashboardModuleUnavailable,
} from '../sections/admin-dashboard/hooks/dashboardModuleAvailability'

const LAST_SEEN_RELEASE_STORAGE_KEY = 'lastSeenRelease'

export type ReleaseNotesDialogState = {
  open: boolean
  currentRelease: string
  previousRelease: string
  notesHtml: string
}

const emptyReleaseNotesDialog = (): ReleaseNotesDialogState => ({
  open: false,
  currentRelease: '',
  previousRelease: '',
  notesHtml: '',
})

export const useReleaseNotesDialog = ({
  app,
  canReadReleaseNotes,
  shouldShowSetup,
  checkingSetup,
}: {
  app: AdminAppState
  canReadReleaseNotes: boolean
  shouldShowSetup: boolean
  checkingSetup: boolean
}) => {
  const [releaseNotesDialog, setReleaseNotesDialog] = useState<ReleaseNotesDialogState>(emptyReleaseNotesDialog)

  useEffect(() => {
    const token = app.token
    if (
      !app.isAuthenticated ||
      !canReadReleaseNotes ||
      shouldShowSetup ||
      checkingSetup ||
      typeof window === 'undefined'
    ) {
      return
    }
    if (typeof token !== 'string' || !token) {
      return
    }

    let cancelled = false

    const loadReleaseNotes = async () => {
      if (isDashboardModuleUnavailable('release_notes')) {
        return
      }
      try {
        const payload = await fetchAdminReleaseNotes(token)
        markDashboardModuleAvailable('release_notes')
        if (cancelled) return
        if (!payload.current) return

        const lastSeenRelease = window.localStorage.getItem(LAST_SEEN_RELEASE_STORAGE_KEY) ?? ''
        if (lastSeenRelease === payload.current) return

        const { renderMarkdownToHtml } = await import('../sections/shop/utils/markdown')
        if (cancelled) return

        setReleaseNotesDialog({
          open: true,
          currentRelease: payload.current,
          previousRelease: payload.previous,
          notesHtml: renderMarkdownToHtml(payload.notes),
        })
      } catch (error) {
        if (isModuleUnavailableError(error)) {
          markDashboardModuleUnavailable('release_notes')
        }
      }
    }

    void loadReleaseNotes()

    return () => {
      cancelled = true
    }
  }, [app.isAuthenticated, app.token, canReadReleaseNotes, checkingSetup, shouldShowSetup])

  const acknowledgeReleaseNotes = () => {
    if (typeof window !== 'undefined' && releaseNotesDialog.currentRelease) {
      window.localStorage.setItem(LAST_SEEN_RELEASE_STORAGE_KEY, releaseNotesDialog.currentRelease)
    }
    setReleaseNotesDialog((prev) => ({ ...prev, open: false }))
  }

  return { releaseNotesDialog, acknowledgeReleaseNotes }
}
